import { useEffect, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useAddSubscriberToTenantMutation } from '@features/tenants/api/tenantsApi';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { logger } from '@/utils/logger';

const log = logger.scope('[CallbackPage]');

/**
 * Consume one-shot subscriber key stored by LoginPage / InvitationRedirect.
 * Only returns keys that need a frontend API call (subscriber keys).
 * invitationKey is handled by Auth0 Rules/Actions during authentication — no API call needed.
 */
const consumeSubscriberKey = (): string | null => {
  const subscriber = sessionStorage.getItem('invite_subscriber_key');
  if (subscriber) {
    sessionStorage.removeItem('invite_subscriber_key');
    log.info('Consumed subscriber key from sessionStorage:', subscriber);
    return subscriber;
  }
  return null;
};

/**
 * Clean up invitationKey from sessionStorage (handled by Auth0, no API call needed).
 */
const cleanupInvitationKey = (): void => {
  const invitation = sessionStorage.getItem('invite_invitation_key');
  if (invitation) {
    sessionStorage.removeItem('invite_invitation_key');
    log.info('Cleaned up invitationKey from sessionStorage (processed by Auth0 Rule):', invitation);
  }
};

export const CallbackPage = () => {
  const { isAuthenticated, isLoading, error, user } = useAuth0();
  const navigate = useNavigate();
  const [addSubscriber] = useAddSubscriberToTenantMutation();
  const [statusMessage, setStatusMessage] = useState('Completing sign in...');
  const processedRef = useRef(false);

  useEffect(() => {
    if (isLoading || processedRef.current) return;

    if (error) {
      log.error('Auth0 callback error:', error);
      navigate('/login', { replace: true });
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    processedRef.current = true;

    const finishRedirect = () => {
      const encodedReturnTo = sessionStorage.getItem('auth_return_to');
      sessionStorage.removeItem('auth_return_to');

      let returnTo = '/';
      if (encodedReturnTo) {
        try {
          const decoded = atob(encodedReturnTo);
          const url = new URL(decoded, window.location.origin);
          url.searchParams.delete('subscriber');
          url.searchParams.delete('invitationKey');
          const clean = url.pathname + (url.search || '');
          returnTo = clean === '/' || clean === '/?' ? '/' : clean;
        } catch {
          returnTo = '/';
        }
      }

      log.info('Redirecting to:', returnTo);
      navigate(returnTo, { replace: true });
    };

    // Clean up invitationKey (handled server-side by Auth0 Rule/Action during authentication)
    cleanupInvitationKey();

    // Process subscriber key if one was stored before Auth0 redirect.
    // subscriber keys require a frontend API call to addnewsubscriberexistingusertotenant.
    const subscriberKey = consumeSubscriberKey();
    if (subscriberKey) {
      setStatusMessage('Accepting invitation...');
      addSubscriber(subscriberKey)
        .unwrap()
        .then(() => {
          finishRedirect();
        })
        .catch((err) => {
          log.error('addSubscriberToTenant failed:', err);
          finishRedirect();
        });
    } else {
      finishRedirect();
    }
  }, [isAuthenticated, isLoading, error, navigate, addSubscriber, user]);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.default',
          gap: 2,
        }}
      >
        <Typography variant="h6" color="error">
          Authentication Error
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        {statusMessage}
      </Typography>
    </Box>
  );
};
